// Dove trovare la bottega: indirizzo, visite, orari e mappa (OpenStreetMap, senza cookie
// di profilazione). Mostra solo ciò che l'artigiano ha scelto di rendere pubblico.
import React from 'react';
import { Clock, MapPin, Navigation, DoorOpen, CalendarCheck } from 'lucide-react';
import { useShop } from './ShopContext';

export const VISIT_LABELS = {
  none: null,
  appointment: { icon: CalendarCheck, text: 'Visite in laboratorio su appuntamento' },
  open: { icon: DoorOpen, text: 'Laboratorio aperto al pubblico' },
};

export const contactLines = (c) => {
  if (!c) return null;
  const street = c.visibility === 'address' ? c.address : '';
  const place = [c.visibility === 'address' ? c.postal_code : '', c.city, c.province ? `(${c.province})` : ''].filter(Boolean).join(' ');
  return { street, place };
};

export const directionsUrl = (c) => {
  if (c?.lat != null && c?.lon != null) return `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lon}`;
  const q = [c?.address, c?.postal_code, c?.city, 'Italia'].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

export function ShopMap({ contact, className = '' }) {
  const { style } = useShop();
  if (contact?.lat == null || contact?.lon == null) return null;
  const d = 0.008;
  const bbox = [contact.lon - d * 1.6, contact.lat - d, contact.lon + d * 1.6, contact.lat + d].join(',');
  return (
    <div className={`relative overflow-hidden bg-slate-100 ${style.media} ${className}`} data-testid="shop-map">
      <iframe
        title="Mappa della bottega"
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${contact.lat},${contact.lon}`}
        className="absolute inset-0 w-full h-full border-0"
        loading="lazy"
      />
    </div>
  );
}

export default function ShopLocation({ compact = false }) {
  const { branding, style, name } = useShop();
  const c = branding.contact;
  if (!c) return null;
  const lines = contactLines(c);
  const visit = VISIT_LABELS[c.visits];
  const hasMap = c.visibility === 'address' && c.lat != null;

  const details = (
    <div className="space-y-4">
      <p className="flex gap-3 text-slate-700">
        <MapPin className="w-5 h-5 text-shop-primary shrink-0 mt-0.5" />
        <span>
          <span className="block font-semibold text-slate-900">{name}</span>
          {lines.street && <span className="block">{lines.street}</span>}
          <span className="block">{lines.place}</span>
        </span>
      </p>
      {visit && (
        <p className="flex gap-3 text-slate-700"><visit.icon className="w-5 h-5 text-shop-primary shrink-0" />{visit.text}</p>
      )}
      {c.hours && (
        <p className="flex gap-3 text-slate-700"><Clock className="w-5 h-5 text-shop-primary shrink-0 mt-0.5" /><span className="whitespace-pre-line">{c.hours}</span></p>
      )}
      {c.note && <p className="text-sm text-slate-500">{c.note}</p>}
      {c.visibility === 'address' && (
        <a
          href={directionsUrl(c)}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 px-5 py-2.5 ${style.btn} border border-shop-primary text-shop-primary text-sm font-medium hover:bg-shop-primary/5`}
          data-testid="shop-directions"
        >
          <Navigation className="w-4 h-4" /> Indicazioni stradali
        </a>
      )}
    </div>
  );

  if (!hasMap) return <div className={`p-6 ${style.panel}`} data-testid="shop-location">{details}</div>;
  return (
    <div className={`grid gap-6 ${compact ? 'md:grid-cols-2' : 'lg:grid-cols-5'} items-stretch`} data-testid="shop-location">
      <div className={`p-6 ${style.panel} ${compact ? '' : 'lg:col-span-2'}`}>{details}</div>
      <ShopMap contact={c} className={`min-h-[260px] ${compact ? '' : 'lg:col-span-3'}`} />
    </div>
  );
}
